import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle } from "lucide-react";
import { OWNER } from "@/lib/legalContent";
import { LEGAL } from "@/constants/testIds";

const SERVICES = [
  "Quiropodia",
  "Uñas encarnadas",
  "Estudio biomecánico",
  "Plantillas personalizadas",
  "Podología deportiva",
  "Pie diabético",
  "Podología infantil",
  "Papilomas (verrugas)",
  "Tratamiento con láser",
  "Asesoramiento quirúrgico",
  "Test epigenético",
  "Primera valoración",
];

export default function ContactForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!name.trim()) next.name = "Indica tu nombre completo.";
    if (!phone.trim()) next.phone = "Indica un teléfono de contacto.";
    if (!accepted) next.accepted = "Debes aceptar la Política de Privacidad para continuar.";
    setErrors(next);
    if (Object.keys(next).length > 0) {
      if (next.accepted) toast.error("Debes aceptar la Política de Privacidad.");
      return;
    }

    const text =
      `Hola Lorena, soy ${name.trim()}.` +
      (service ? ` Me interesa: ${service}.` : "") +
      (message.trim() ? ` ${message.trim()}` : "") +
      ` Mi teléfono es ${phone.trim()}. Me gustaría solicitar una cita.`;
    const url = `https://wa.me/${OWNER.whatsapp}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Abriendo WhatsApp con tu mensaje preparado…");
  };

  const inputCls =
    "w-full rounded-xl border border-input bg-white px-4 py-3 text-sm text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20";

  return (
    <form onSubmit={handleSubmit} data-testid={LEGAL.contactForm} noValidate className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-semibold text-foreground">Nombre completo *</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid={LEGAL.contactName}
          className={inputCls}
          placeholder="Tu nombre"
        />
        {errors.name && <p className="mt-1 text-xs font-medium text-destructive">{errors.name}</p>}
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-semibold text-foreground">Teléfono *</label>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid={LEGAL.contactPhone}
          className={inputCls}
          placeholder="600 000 000"
          inputMode="tel"
        />
        {errors.phone && <p className="mt-1 text-xs font-medium text-destructive">{errors.phone}</p>}
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-semibold text-foreground">Servicio</label>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger data-testid={LEGAL.contactService} className={inputCls + " h-auto"}>
            <SelectValue placeholder="Selecciona un servicio (opcional)" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-semibold text-foreground">Mensaje</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid={LEGAL.contactMessage}
          rows={3}
          className={inputCls + " resize-none"}
          placeholder="Cuéntanos brevemente tu caso (opcional)"
        />
      </div>

      <div className="flex items-start gap-3 rounded-xl bg-secondary/50 p-3.5">
        <Checkbox
          id="privacy"
          checked={accepted}
          onCheckedChange={(v) => setAccepted(!!v)}
          data-testid={LEGAL.contactPrivacyCheckbox}
          className="mt-0.5"
        />
        <label htmlFor="privacy" className="text-sm leading-relaxed text-muted-foreground">
          He leído y acepto la{" "}
          <Link
            to="/politica-de-privacidad"
            target="_blank"
            data-testid={LEGAL.contactPrivacyLink}
            className="font-semibold text-primary underline underline-offset-2"
          >
            Política de Privacidad
          </Link>
          . *
        </label>
      </div>
      {errors.accepted && <p className="-mt-2 text-xs font-medium text-destructive">{errors.accepted}</p>}

      <button
        type="submit"
        data-testid={LEGAL.contactSubmit}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-lg"
      >
        <MessageCircle className="h-4 w-4" />
        Enviar por WhatsApp
      </button>
      <p className="text-center text-xs text-muted-foreground">
        Al enviar, abriremos WhatsApp con tu mensaje preparado.
      </p>
    </form>
  );
}
